import { useEffect } from 'react';

/**
 * Turns raw video <iframe> embeds inside CMS-authored HTML (rendered via
 * dangerouslySetInnerHTML) into a proper "click to play" block.
 *
 *   const containerRef = useRef(null);
 *   useVideoEmbeds(containerRef, [event?.id]);
 *   <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />
 *
 * Why: partner-authored descriptions sometimes embed a bare Vimeo/YouTube
 * iframe at a small fixed width (e.g. 360x296). CSS alone (aspect-video,
 * w-full) can make the *box* responsive, but the player's own chrome
 * (title bar, "⋮" menu, timestamp, tiny play button) was laid out for that
 * original small size and just looks cramped — it doesn't reflow. So
 * instead of showing the live embed at all on first paint, we replace each
 * iframe with a lightweight thumbnail + centered play button that matches
 * the app's visual language. The real iframe (with autoplay) is only
 * inserted into the DOM once the user clicks — which also means we don't
 * pay for the embed's JS/network cost until the user actually wants to
 * watch, and the placeholder always reads as "a real video" no matter how
 * small the original iframe's width/height attributes were.
 *
 * Supports Vimeo and YouTube; any other iframe embed still gets the same
 * generic dark-overlay + play-button treatment (no thumbnail fetch), so
 * the fix degrades gracefully rather than skipping unknown providers.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - element that holds
 *   the dangerouslySetInnerHTML content.
 * @param {Array} deps - effect deps; re-run when the HTML content changes
 *   (e.g. [event?.id] or [html]).
 */
export default function useVideoEmbeds(containerRef, deps = []) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const iframes = Array.from(container.querySelectorAll('iframe'));
    const cleanups = [];

    iframes.forEach((iframe) => {
      const src = iframe.getAttribute('src') || '';
      if (!isVideoEmbed(src)) return;

      const cleanup = mountPlaceholder(iframe, src);
      if (cleanup) cleanups.push(cleanup);
    });

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function isVideoEmbed(src) {
  return /vimeo\.com|youtube\.com|youtube-nocookie\.com|youtu\.be/i.test(src);
}

function normalizeSrc(src) {
  // Protocol-relative CMS markup ("//player.vimeo.com/...") doesn't load
  // as a standalone iframe src in every context — make it explicit https.
  if (src.startsWith('//')) return `https:${src}`;
  return src;
}

function withAutoplay(src) {
  const normalized = normalizeSrc(src);
  const sep = normalized.includes('?') ? '&' : '?';
  if (/youtube\.com|youtu\.be/i.test(normalized)) {
    return `${normalized}${sep}autoplay=1&rel=0`;
  }
  // Vimeo
  return `${normalized}${sep}autoplay=1`;
}

function vimeoId(src) {
  const m = src.match(/player\.vimeo\.com\/video\/(\d+)/i);
  return m ? m[1] : null;
}

function youtubeId(src) {
  let m = src.match(/youtube(?:-nocookie)?\.com\/embed\/([\w-]+)/i);
  if (m) return m[1];
  m = src.match(/youtu\.be\/([\w-]+)/i);
  return m ? m[1] : null;
}

function mountPlaceholder(iframe, rawSrc) {
  const src = normalizeSrc(rawSrc);
  const parent = iframe.parentNode;
  if (!parent) return null;

  const placeholder = document.createElement('button');
  placeholder.type = 'button';
  placeholder.setAttribute('aria-label', 'Play video');
  placeholder.className =
    'group relative flex aspect-video w-full max-w-xl items-center justify-center overflow-hidden rounded-xl bg-slate-900 text-left shadow-card';

  const ytId = youtubeId(src);
  const thumbUrl = ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null;

  const mountThumb = (url) => {
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    img.className = 'absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity duration-200 group-hover:opacity-100';
    placeholder.insertBefore(img, placeholder.firstChild);
  };

  if (thumbUrl) {
    mountThumb(thumbUrl);
  } else if (vimeoId(src)) {
    // Vimeo has no predictable static thumbnail URL pattern (unlike
    // YouTube's img.youtube.com/vi/<id>/...), so fetch one via its public
    // oEmbed endpoint. Best-effort: if it fails (network, private video,
    // ad-blocker), the placeholder just stays on the plain dark-scrim
    // treatment — still fully usable, matching the "unknown provider"
    // fallback below.
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(src.replace(/^\/\//, 'https://'))}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (json?.thumbnail_url && placeholder.isConnected) {
          mountThumb(json.thumbnail_url);
        }
      })
      .catch(() => {});
  }

  // Dark scrim so the play button stays legible over any thumbnail/color.
  const scrim = document.createElement('span');
  scrim.className = 'absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/30';
  placeholder.appendChild(scrim);

  // Centered circular play button — matches the WhatsApp floating button /
  // carousel arrow-button treatment (rounded-full, shadow-elevated, scale
  // on hover).
  const playBtn = document.createElement('span');
  playBtn.className =
    'relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-brand-500 shadow-elevated transition-transform duration-200 ease-out-quint group-hover:scale-110 group-hover:bg-accent-500 group-hover:text-brand-900 sm:h-16 sm:w-16';
  playBtn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="currentColor" class="h-6 w-6 translate-x-0.5 sm:h-7 sm:w-7" aria-hidden="true"><path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14Z"/></svg>';
  placeholder.appendChild(playBtn);

  const provider = vimeoId(src) ? 'Vimeo' : youtubeId(src) ? 'YouTube' : 'Video';
  const label = document.createElement('span');
  label.className =
    'absolute bottom-3 left-3 z-10 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white';
  label.textContent = provider;
  placeholder.appendChild(label);

  const handleClick = () => {
    const realIframe = document.createElement('iframe');
    realIframe.src = withAutoplay(src);
    // The page's CSS fallback for un-processed embeds ([&_iframe]:h-auto
    // [&_iframe]:aspect-video etc. on the .prose container) is a descendant
    // selector, so it also matches THIS dynamically-injected iframe — and
    // fights our absolute/inset-0/h-full sizing with equal specificity,
    // occasionally letting `h-auto` collapse the iframe back down to a tiny
    // intrinsic size (the exact "small player" regression this hook exists
    // to prevent). `!` (important) utilities guarantee our sizing wins.
    realIframe.className = 'absolute inset-0 !h-full !w-full !max-w-none !aspect-auto rounded-xl';
    realIframe.setAttribute('frameBorder', '0');
    realIframe.setAttribute(
      'allow',
      'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media'
    );
    realIframe.setAttribute('allowfullscreen', 'true');
    realIframe.setAttribute('title', `${provider} video player`);

    const wrapper = document.createElement('div');
    wrapper.className = 'relative aspect-video w-full max-w-xl overflow-hidden rounded-xl';
    wrapper.appendChild(realIframe);

    placeholder.replaceWith(wrapper);
  };

  placeholder.addEventListener('click', handleClick);
  parent.replaceChild(placeholder, iframe);

  // Cleanup: if the effect re-runs / unmounts, drop the listener. We don't
  // attempt to restore the original iframe node — the container's HTML is
  // fully re-set by the caller's dangerouslySetInnerHTML on next render.
  return () => placeholder.removeEventListener('click', handleClick);
}
